import i18n from '@/utils/i18n';
import React from 'react';
import {
  ChatBubble,
  ChatMessage,
  Icons,
  Typography,
} from '@usepepper/sprout-kit-ui';
import {
  CopilotMessage,
  CopilotMessageStatus,
} from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/blocs/stores/use-copilot-session-store';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { formatTime } from '@/utils/date';
import { StyleProp, View, ViewStyle } from 'react-native';

const META_ICON_SIZE = 12;
const RETRY_ICON_SIZE = 14;

interface CopilotMessageBubbleProps {
  message: CopilotMessage;
  onRetry?: () => void;
  showTail?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const CopilotMessageBubble: React.FC<CopilotMessageBubbleProps> = ({
  message,
  onRetry,
  showTail = true,
  style,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();
  const isUser = message.role === 'user';
  const isFailed = message.status === CopilotMessageStatus.Failed;
  const time = formatTime(message.timestamp);

  return (
    <View
      accessibilityLabel={
        isUser ? undefined : i18n.t('sales_portal.copilot.message_from_copilot')
      }
      style={[styles.row, isUser ? styles.rowEnd : styles.rowStart, style]}
    >
      <ChatBubble
        showTail={showTail}
        variant={isUser ? 'sent' : 'received'}
        // SK-UI resolves style props in JSX order, so the error override
        // must come after `variant` to beat its backgroundColor
        {...(isFailed && { backgroundColor: '$color.bgError' })}
      >
        <>
          <ChatBubble.Text>{message.text}</ChatBubble.Text>
          {time ? (
            <View style={styles.metaRow}>
              <Typography color="$textTertiary" size="$xs">
                {time}
              </Typography>
              {isFailed ? (
                <Icons.AlertCircle
                  color={theme.sk.color.icon.negative.primary}
                  size={META_ICON_SIZE}
                />
              ) : null}
            </View>
          ) : null}
        </>
      </ChatBubble>
      {isFailed ? (
        <ChatMessage.FailedAction
          icon={
            <Icons.RotateCcw
              color={theme.sk.color.icon.negative.primary}
              size={RETRY_ICON_SIZE}
            />
          }
          label={i18n.t('sales_portal.copilot.not_delivered')}
          onRetry={onRetry}
        >
          {i18n.t('sales_portal.copilot.not_delivered')}
        </ChatMessage.FailedAction>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  metaRow: {
    alignItems: 'center',
    alignSelf: 'flex-end',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  row: {
    width: '100%',
  },
  rowEnd: {
    alignItems: 'flex-end',
  },
  rowStart: {
    alignItems: 'flex-start',
  },
}));
